import {
    Component,
    EventEmitter,
    forwardRef,
    HostListener,
    Input, OnDestroy,
    OnInit,
    Output,
    signal,
    SimpleChanges,
    ViewChild
} from "@angular/core";
import { ControlValueAccessor, UntypedFormBuilder, UntypedFormGroup, NG_VALUE_ACCESSOR, Validators } from "@angular/forms";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { PatientBasicInfo } from "@main/app-layout/components/patient-switch/patient";
import { PatientService } from "@main/app-layout/components/patient-switch/patient.service";
import { PatientLogin, PictureMode } from "@main/users";
import CryptoES from "crypto-es";
import { OverlayPanel } from "primeng/overlaypanel";
import { CoreRole } from "@main/roles";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { PlayerOfflineStorage } from "@project/player/player-offline.storage";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { PatientSuggestion } from "@project/models/patient-suggestion";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject, takeUntil } from "rxjs";

@Component({
    selector: "patient-select",
    templateUrl: "./patient-select.component.html",
    styleUrls: ["./patient-select.component.scss"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PatientSelectComponent),
            multi: true
        }
    ]
})
export class PatientSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
    defaultAvatar = "assets/images/noavatar.png";
    patientAvatarUrl: string = this.defaultAvatar;
    loadingPatientAvatar: boolean = true;
    patients: PatientSuggestion[] = [];
    selectedPatient: PatientSuggestion;
    selectedPatientInfo: PatientBasicInfo;
    showDialog: boolean;

    private readonly destroyed$ = new Subject<void>();

    controlInitialized = signal(false);
    loadingPatient = signal(false);

    form: UntypedFormGroup = this.fb.group({
        patient: [null]
    });
    private _patientsFromAppointments: PatientSuggestion[];

    get size() {
        return this.slim ? "1.3rem" : "2.2rem";
    }
    @Input() autoSelectNewPatient: boolean;

    @Input() showAddButton: boolean;

    private _inputDisabled = false;
    private _formDisabled = false;

    get disabled(): boolean {
        return this._inputDisabled || this._formDisabled;
    }

    @Input()
    set disabled(value: boolean) {
        this._inputDisabled = value;
        this.applyDisabledState();
    }

    private applyDisabledState(): void {
        const patientControl = this.form.get("patient");
        if (this.disabled) {
            patientControl.disable({ emitEvent: false });
        } else {
            patientControl.enable({ emitEvent: false });
        }
    }

    @Input() readonly: boolean;

    @Input() disableAdmins: boolean = false;

    @Input() anonymization: boolean = false;

    @Input() required: boolean = false;

    @Input()
    set patientsFromAppointments(value: PatientSuggestion[]) {
        this._patientsFromAppointments = value;
        this.updatePatientsList();
    }

    @Input() fromAppointmentsFilter: boolean = false;

    private _selectedPatientId: string;

    @Input()
    set selectedPatientId(value: string) {
        this._selectedPatientId = value;
        this.updatePatientsList();
    }

    @Input() slim = false;

    @Output() readonly patientSelected: EventEmitter<PatientLogin | { id: string; fullName: string }> = new EventEmitter<PatientLogin>();

    @ViewChild("basicinfo") basicInfo: OverlayPanel;

    constructor(
        private patientService: PatientService,
        private fb: UntypedFormBuilder,
        private broadcastService: BroadcastService,
        private appOnlineStateService: AppOnlineStateService,
        public namingConvService: NamingConventionService
    ) {}

    onModelChange: Function = () => {};
    onModelTouched: Function = () => {};

    updateModel(): void {
        this.onModelChange(this.selectedPatient?.patientId || this.selectedPatient?.id);
        this.onModelTouched();
    }

    registerOnChange(fn: Function): void {
        this.onModelChange = fn;
    }

    registerOnTouched(fn: Function): void {
        this.onModelTouched = fn;
    }

    writeValue(patientId: string): void {
        if (!this.controlInitialized()) {
            this._selectedPatientId = patientId;
        } else {
            this.updateSelectedPatient(patientId);
        }
    }

    setDisabledState?(isDisabled: boolean): void {
        this._formDisabled = isDisabled;
        this.applyDisabledState();
    }

    async ngOnInit(): Promise<void> {
        await this.updatePatientsList();
        this.broadcastService.on<string>(PatientService.PatientsUpdatedEventName)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(patientId => {
                if (this.autoSelectNewPatient) {
                    this._selectedPatientId = patientId;
                }
                this.updatePatientsList();
            });

        this.controlInitialized.set(true);
    }

    ngOnDestroy() {
        this.destroyed$.next();
    }

    onPatientSelect(event: any) {
        this.getBasicInfo();
        this.updateModel();
    }

    getBasicInfo() {
        if (!this.selectedPatient) {
            this.patientSelected.emit(null);
            return;
        }

        try {
            this.loadingPatient.set(true);
            this.loadingPatientAvatar = false; // Temporary

            var patientInfoPlaceholder = {
                id: this.selectedPatient.patientId ?? this.selectedPatient.id,
                patientId: this.selectedPatient.patientId,
                fullName: this.selectedPatient.fullName,
                miProNumber: this.selectedPatient.miProNumber
            };

            if (PlayerOfflineStorage.get(patientInfoPlaceholder.id) != null || !this.appOnlineStateService.isAppOnline) {
                this.selectedPatientInfo = patientInfoPlaceholder;
                this.patientSelected.emit(patientInfoPlaceholder); // For offline support
            } else {
                this.patientService.getPatientBasicInfo(this.selectedPatient.patientId ?? this.selectedPatient.id).then(data => {
                    this.selectedPatientInfo = {...data, ...patientInfoPlaceholder};
                    this.patientSelected.emit(this.selectedPatientInfo);
                });
            }
        } finally {
            this.loadingPatient.set(false);
        }
    }

    setPatientAvatar() {
        switch (this.selectedPatientInfo.pictureMode) {
            case PictureMode.Empty:
                this.patientAvatarUrl = this.defaultAvatar;
                break;
            case PictureMode.Gravatar:
                this.patientAvatarUrl =
                    "https://www.gravatar.com/avatar/" + CryptoES.MD5(this.selectedPatientInfo.gravatarEmail).toString();
                break;
            case PictureMode.Upload:
                this.patientAvatarUrl =
                    this.selectedPatientInfo.avatarImage != null ? this.selectedPatientInfo.avatarImage.url : this.defaultAvatar;
                break;
        }
        this.loadingPatientAvatar = false;
    }

    @HostListener("mouseout", ["$event.target"])
    onMouseOut(target) {
        if (target.id.length > 0) {
            this.basicInfo.hide();
        }

        return false;
    }

    updateSelectedPatient(patientId: string) {
        this.selectedPatient = this.patients.find(p => p.patientId === patientId || p.id === patientId);
        this.form.get("patient").patchValue(this.selectedPatient);
        this.getBasicInfo();
        this.updateModel();
    }

    showOverlayPanel(event: any, target: any) {
        if (!this.anonymization) {
            this.basicInfo.show(event, target);
        }
    }

    private async updatePatientsList() {
        try {
            this.loadingPatient.set(true);

            let data = <PatientSuggestion[]>[];

            if (this.fromAppointmentsFilter) {
                data = this._patientsFromAppointments;
            } else {
                const allPatientsData = await this.patientService.getAll();
                data = allPatientsData.patients;
            }

            this.patients = this.disableAdmins ? (data ? data.filter(x => !this.isAdmin(x.fullName)) : []) : data;
            if (this._selectedPatientId) {
                this.updateSelectedPatient(this._selectedPatientId);
            }
        } finally {
            this.loadingPatient.set(false);
        }
    }

    private isAdmin(fullName: string) {
        fullName = this.processFullName(fullName);
        return fullName === this.processFullName(CoreRole.MiProAdmin) || fullName === this.processFullName(CoreRole.SuperAdmin);
    }

    private processFullName(fullname: string): string {
        return fullname.toLowerCase().trim().replace(" ", "");
    }

    async patientCreated(event: PatientLogin) {
        this._selectedPatientId = event.patientId ?? event.id;
        this.showDialog = false;
        await this.updatePatientsList();
    }

    markAsTouchedAndDirty() {
        const ctrl = this.form.get("patient");
        ctrl?.markAsTouched();
        ctrl?.markAsDirty();
        ctrl?.updateValueAndValidity();
    }
}
